import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Box, styled,
  Switch, Typography, Chip, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getAllOrganizations, deleteOrganization, updateOrganizationPaidStatus, updateOrganizationUserLimit } from '../../Services/BorgAPIClient';
import moment from 'moment';
import ChangeConfirmationDialog from '../../Components/ChangeConfirmationDialog';
import StartTrialModal from '../../Components/Admin/StartTrialModal';
import EditTrialModal from '../../Components/Admin/EditTrialModal';
import { useDebouncedCallback } from "use-debounce";

type Organization = {
  id: string;
  name: string;
  createdDate: Date;
  isPaid: boolean;
  userLimit: number;
  accountsCreated: number;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
};

function isTrialActive(org: Organization): boolean {
  if (!org.trialEndDate) return false;
  const end = new Date(org.trialEndDate);
  return end > new Date();
}

function isTrialEnded(org: Organization): boolean {
  if (!org.trialEndDate) return false;
  return new Date(org.trialEndDate) <= new Date();
}

const CustomTableHead = styled(TableHead)({
  backgroundColor: 'black',
  '& th': {
    color: 'white',
    cursor: 'pointer',
  },
});

const OrganizationsAdminPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null);
  const [organizationForTrial, setOrganizationForTrial] = useState<Organization | null>(null);
  const [startTrialModalOpen, setStartTrialModalOpen] = useState(false);
  const [editTrialModalOpen, setEditTrialModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<keyof Organization>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchOrganizations = useCallback(async () => {
    try {
      const result = await getAllOrganizations();
      setOrganizations(result.data as Organization[]);
      setIsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSort = (column: keyof Organization) => {
    const isAsc = sortBy === column && sortDirection === 'asc';
    setSortBy(column);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  const sortedOrganizations = [...organizations].sort((a, b) => {
    const compareResult = (aVal: any, bVal: any) => {
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    };

    const result = compareResult(a[sortBy], b[sortBy]);
    return sortDirection === 'asc' ? result : -result;
  });

  const closeConfirmDeleteDialog = () => {
    setOpenDialog(false);
  };

  const handleDeleteClick = (organization: Organization) => {
    setOrganizationToDelete(organization);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (organizationToDelete) {
      await deleteOrganization(organizationToDelete.id);
      fetchOrganizations();
    }
    setOpenDialog(false);
    setOrganizationToDelete(null);
  };

  const handleTogglePaidStatus = async (organization: Organization) => {
    await updateOrganizationPaidStatus(organization.id, !organization.isPaid);
    fetchOrganizations();
  };

  const debouncedUpdateUserLimit = useDebouncedCallback(
    async (organizationId: string, userLimit: number) => {
      try {
        await updateOrganizationUserLimit(organizationId, userLimit);
      } catch (err) {
        console.error("Failed to update user limit", err);

        // Optional: revert the value or show an error toast
      }
    },
    500 // wait 500ms after the last change
  );

  const handleUserLimitChange = (
    organization: Organization,
    newValue: number | null
  ) => {
    if (newValue == null || Number.isNaN(newValue)) {
      return;
    }

    // Update UI immediately
    setOrganizations(prev =>
      prev.map(org =>
        org.id === organization.id
          ? { ...org, userLimit: newValue }
          : org
      )
    );

    // Debounced API call
    debouncedUpdateUserLimit(organization.id, newValue);
  };


  const handleTrialClick = (organization: Organization) => {
    setOrganizationForTrial(organization);
    if (isTrialActive(organization))
      setEditTrialModalOpen(true);
    else
      setStartTrialModalOpen(true);
  };

  const closeTrialModals = () => {
    setOrganizationForTrial(null);
    setStartTrialModalOpen(false);
    setEditTrialModalOpen(false);
  };

  const handleTrialConfirm = () => {
    fetchOrganizations();
    closeTrialModals();
  };

  const renderPaymentStatus = (organization: Organization) => {
    const active = isTrialActive(organization);
    const ended = isTrialEnded(organization);
    if (organization.isPaid && !active) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label="Paid" color="success" size="small" />
          <Switch checked={true} onChange={() => handleTogglePaidStatus(organization)} color="primary" />
        </Box>
      );
    }
    if (active) {
      const end = new Date(organization.trialEndDate!);
      const duration = moment.duration(moment(end).diff(moment()));
      const months = Math.floor(duration.asMonths());
      const days = Math.floor(duration.asDays());
      const text = months >= 1 ? `Trial ends in ${months} month${months !== 1 ? 's' : ''}` : `Trial ends in ${days} day${days !== 1 ? 's' : ''}`;
      return <Chip label={text} color="primary" size="small" />;
    }
    if (ended && !organization.isPaid) {
      const end = new Date(organization.trialEndDate!);
      const ago = moment(end).fromNow();
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label="Unpaid" color="error" size="small" />
            <Switch checked={false} onChange={() => handleTogglePaidStatus(organization)} color="primary" />
          </Box>
          <Typography variant="caption" color="text.secondary">Trial ended {ago}</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip label="Unpaid" color="error" size="small" />
        <Switch checked={false} onChange={() => handleTogglePaidStatus(organization)} color="primary" />
      </Box>
    );
  };

  if (isLoaded && organizations.length > 0) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          borderRadius: 1,
          marginY: 2,
          bgcolor: 'primary.dark',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader>
              <CustomTableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort('name')}>
                    Organization Name {sortBy === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell onClick={() => handleSort('createdDate')}>
                    Created Date {sortBy === 'createdDate' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell onClick={() => handleSort('isPaid')}>
                    Payment Status {sortBy === 'isPaid' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell onClick={() => handleSort('accountsCreated')}>
                    # Accounts Created {sortBy === 'accountsCreated' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell onClick={() => handleSort('userLimit')}>
                    User Limit {sortBy === 'userLimit' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {sortedOrganizations.map((organization, index) => (
                  <TableRow
                    key={index}
                    style={{
                      backgroundColor: organization.isPaid || isTrialActive(organization) ? '' : '#8e5d64',
                    }}
                  >
                    <TableCell>{organization.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={`Created ${moment(organization.createdDate).fromNow()}`}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {renderPaymentStatus(organization)}
                    </TableCell>
                    <TableCell>{organization.accountsCreated ?? 0}</TableCell>
                    <TableCell>
                      {organization.userLimit !== null && (
                        <TextField
                          type="number"
                          value={organization.userLimit}
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);

                            handleUserLimitChange(organization, value);
                          }}
                          inputProps={{
                            min: 5,
                            max: 1000,
                            step: 5,
                          }}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          startIcon={<AccessTimeIcon />}
                          onClick={() => handleTrialClick(organization)}
                        >
                          Trial
                        </Button>
                        <Button
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(organization)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <ChangeConfirmationDialog
          title="Confirm Delete"
          message={`This action cannot be undone. Are you sure you want to delete "${organizationToDelete?.name}"?`}
          open={openDialog}
          onClose={closeConfirmDeleteDialog}
          onCancel={closeConfirmDeleteDialog}
          onConfirm={handleConfirmDelete}
        />
        <StartTrialModal
          open={startTrialModalOpen}
          onClose={closeTrialModals}
          onConfirm={handleTrialConfirm}
          organizationId={organizationForTrial?.id ?? ''}
        />
        <EditTrialModal
          open={editTrialModalOpen}
          onClose={closeTrialModals}
          onConfirm={handleTrialConfirm}
          organization={organizationForTrial}
        />
      </Box>
    );
  } else if (isLoaded) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography align='center' variant='h2' sx={{ margin: 10 }}>No Courses Found</Typography>
      </Box>
    );
  } else {
    return <div>Loading...</div>;
  }
};

export default OrganizationsAdminPage;